import React, { useState, useRef, useEffect } from "react"
import { Link } from "gatsby"
import { motion, AnimatePresence } from "framer-motion"
import { StaticImage } from "gatsby-plugin-image"
import MainNavLink from "./main-nav-link"
import Hero from "./hero"
import Logo from "./logo"

const Header = ({ page }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const trigger = useRef(null)
  const mobileNav = useRef(null)

  // close the mobile menu on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!mobileNav.current || !trigger.current) return
      if (
        !mobileNavOpen ||
        mobileNav.current.contains(target) ||
        trigger.current.contains(target)
      )
        return
      setMobileNavOpen(false)
    }
    document.addEventListener("click", clickHandler)
    return () => document.removeEventListener("click", clickHandler)
  })

  // close the mobile menu if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!mobileNavOpen || keyCode !== 27) return
      setMobileNavOpen(false)
    }
    document.addEventListener("keydown", keyHandler)
    return () => document.removeEventListener("keydown", keyHandler)
  })

  const navLinks = [
    {
      title: "Listings",
      to: "/listings",
    },
    {
      title: "Buyers",
      to: "/buyers",
    },
    {
      title: "Sellers",
      to: "/sellers",
    },
    {
      title: "Charter",
      to: "/yachts-for-charter",
    },
    {
      title: "About",
      to: "/about",
    },
    {
      title: "More Luxe",
      to: "/more-luxe",
    },
    {
      title: "Contact",
      to: "/contact",
    },
  ]

  return (
    <>
      <div className={"bg-navy py-2 text-sm"}>
        <div
          className={
            "flex flex-row gap-4 justify-center items-center text-white"
          }
        >
          <div className={"flex flex-row gap-1 items-center justify-center"}>
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              className={"w-5 h-5 stroke-current text-blue"}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
              />
            </svg>{" "}
            <a
              href="tel:+44 7876231207"
              className={"hover:text-blue transition duration-500 ease-in-out"}
            >
              +44 7876 231207
            </a>
          </div>
          <div className={"flex flex-row gap-1"}>
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              className={"w-5 h-5 stroke-current text-blue"}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
            <a
              href={"mailto:info@luxeyachts.co.uk"}
              className={"hover:text-blue transition duration-500 ease-in-out"}
            >
              info@luxeyachts.co.uk
            </a>
          </div>
        </div>
      </div>
      <div className={"relative bg-navy pt-16 lg:pt-64 pb-16"}>
        <div className="absolute inset-0">
          <StaticImage
            className={"w-full h-full object-cover opacity-20"}
            src={"../images/bg.jpg"}
            alt={"Luxe Yachts"}
          />
        </div>

        <div className={"absolute bg-navy-800 inset-0 opacity-30"} />

        <header className={"absolute top-0 w-full py-6 px-6"}>
          <div
            className={"flex flex-row lg:flex-col items-center justify-between"}
          >
            <nav
              className={
                "relative hidden lg:flex lg:flex-grow lg:mt-4 mb-8 z-10"
              }
            >
              <ul className={"flex flex-row gap-12 xl:gap-20"}>
                {navLinks.map((link, index) => (
                  <MainNavLink key={index} title={link.title} to={link.to} />
                ))}
              </ul>
            </nav>

            <Link to={"/"} className={"relative lg:mt-12 z-10"}>
              <Logo className={"h-20 lg:h-24"} variant={"dark"} />
            </Link>

            {/* Hamburger button */}
            <div className="relative flex lg:hidden z-10">
              <button
                ref={trigger}
                className={`hamburger ${mobileNavOpen && "active"}`}
                aria-controls="mobile-nav"
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
              >
                <span className="sr-only">Menu</span>
                <svg
                  className="w-8 h-8 fill-current text-white"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect y="4" width="24" height="2" />
                  <rect y="11" width="24" height="2" />
                  <rect y="18" width="24" height="2" />
                </svg>
              </button>
            </div>
          </div>

          {/*Mobile navigation */}
          <div ref={mobileNav}>
            <AnimatePresence>
              {mobileNavOpen && (
                <motion.nav
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute top-full h-auto left-0 w-full overflow-scroll no-scrollbar bg-navy-900 z-50"
                >
                  <ul>
                    {navLinks.map((link, index) => (
                      <li
                        key={index}
                        className={"border-b last:border-none border-navy-800"}
                      >
                        <Link
                          to={link.to}
                          className={
                            "group flex flex-row items-center justify-start text-sm font-normal tracking-widest text-white uppercase hover:bg-blue-600 transition duration-500 ease-in-out px-6 py-3"
                          }
                        >
                          <span>{link.title}</span>
                          <span
                            className={
                              "text-blue-200 opacity-0 group-hover:opacity-100 transition duration-500 ease-in-out ml-1"
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              className={`w-4 h-4 stroke-current`}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                              />
                            </svg>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.nav>
              )}
            </AnimatePresence>
          </div>
        </header>
        {page === "home" && <Hero />}

        {/* Background image */}
        <div
          className={
            "absolute inset-0 w-full h-full overflow-hidden opacity-20 z-0"
          }
        ></div>
      </div>
    </>
  )
}

export default Header
